import { Link } from "@tanstack/react-router";
import { Phone, Mail, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { contact, locations, surgeries } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import { aestheticTreatments } from "@/lib/aesthetics-data";
import healicLogo from "@/assets/healic-logo.webp.asset.json";

const companyLinks = [
  { to: "/about", label: "About Us" },
  { to: "/careers", label: "Life At Healic" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact Us" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-and-conditions", label: "Terms and Conditions" },
  { to: "/return-and-exchange", label: "Return and Exchange" },
  { to: "/shipping-policy", label: "Shipping Policy" },
] as const;

const serviceLinks = [
  { to: "/aesthetics", label: "Aesthetics & Skin" },
  { to: "/path-lab", label: "Path Lab" },
  { to: "/pharmacy", label: "Pharmacy" },
  { to: "/healic-plus", label: "Healic+ Card" },
  { to: "/diagnostics", label: "Diagnostics" },
] as const;

const gsSlugs = [
  "piles-hemorrhoidectomy",
  "hernia-repair",
  "gallbladder-removal",
  "circumcision",
  "varicose-veins",
];
const entOphSlugs = ["septoplasty", "tonsillectomy", "sinus-surgery-fess", "cataract-surgery"];
const orthoSlugs = ["knee-replacement", "hip-replacement", "fracture-orif", "arthroscopy"];
const gyneSlugs = ["normal-c-section-delivery", "hysterectomy", "ovarian-cyst-removal"];

function surgeryName(slug: string, fallback: string) {
  return surgeries.find((s) => s.slug === slug)?.name ?? fallback;
}

export function SiteFooter() {
  return (
    <footer className="mt-24 bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        {/* Top brand row */}
        <div className="mb-12 flex flex-col gap-6 border-b border-white/10 pb-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <img
              src={healicLogo.url}
              alt="Healic Multispeciality Hospital"
              loading="lazy"
              decoding="async"
              width={1920}
              height={570}
              className="h-10 w-auto brightness-0 invert"
            />
            <p className="max-w-md text-sm opacity-80">
              Parivar jaisi dekhbhal — fixed-price planned surgeries with expert surgeons across
              North India.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <a
              href={`tel:${contact.phoneTel}`}
              className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-4 py-2 hover:bg-white/15"
            >
              <Phone className="h-4 w-4 text-accent" /> {contact.phone}
            </a>
            <a
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-4 py-2 hover:bg-white/15"
            >
              <Mail className="h-4 w-4 text-accent" /> {contact.email}
            </a>
          </div>
        </div>

        {/* Primary columns: Company / Services / Hospitals / Contact */}
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Our Company</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {companyLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-accent">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Services</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {serviceLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-accent">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Hospitals</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {locations.map((l) => (
                <li key={l.slug}>
                  <Link to={locationHref(l.slug)} className="hover:text-accent">
                    {l.city}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mb-4 mt-6 font-display text-base font-semibold">Upcoming Hospitals</h4>
            <ul className="space-y-2 text-sm opacity-90">
              <li>Champawat</li>
              <li>Gorakhpur</li>
              <li>Sonipat</li>
              <li>Meerut</li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Contact Us</h4>
            <ul className="space-y-2 text-sm opacity-90">
              <li>
                Phone:{" "}
                <a href={`tel:${contact.phoneTel}`} className="hover:text-accent">
                  {contact.phone}
                </a>
              </li>
              <li>
                Email:{" "}
                <a href={`mailto:${contact.email}`} className="hover:text-accent">
                  {contact.email}
                </a>
              </li>
              <li>Mon–Sun: 7 AM – 12 PM</li>
            </ul>
          </div>
        </div>

        {/* Surgery categories */}
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">General Surgery</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {gsSlugs.map((slug) => (
                <li key={slug}>
                  <Link to="/surgeries/$slug" params={{ slug }} className="hover:text-accent">
                    {surgeryName(slug, slug)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "general-surgery" }}
                  className="hover:text-accent"
                >
                  Lipoma Removal
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">ENT &amp; Ophthalmology</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {entOphSlugs.map((slug) => (
                <li key={slug}>
                  <Link to="/surgeries/$slug" params={{ slug }} className="hover:text-accent">
                    {surgeryName(slug, slug)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "ent" }}
                  className="hover:text-accent"
                >
                  Nasal Bone Repair
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Orthopaedics</h4>
            <ul className="space-y-2 text-sm opacity-90">
              {orthoSlugs.map((slug) => (
                <li key={slug}>
                  <Link to="/surgeries/$slug" params={{ slug }} className="hover:text-accent">
                    {surgeryName(slug, slug)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "orthopaedics" }}
                  className="hover:text-accent"
                >
                  Trigger Finger Release
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">
              Gynaecology &amp; Obstetrics
            </h4>
            <ul className="space-y-2 text-sm opacity-90">
              {gyneSlugs.map((slug) => (
                <li key={slug}>
                  <Link to="/surgeries/$slug" params={{ slug }} className="hover:text-accent">
                    {surgeryName(slug, slug)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "gynaecology" }}
                  className="hover:text-accent"
                >
                  D&amp;C / MTP
                </Link>
              </li>
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "gynaecology" }}
                  className="hover:text-accent"
                >
                  Endometrial Ablation
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 font-display text-base font-semibold">Cosmetic Gynaecology</h4>
            <ul className="space-y-2 text-sm opacity-90">
              <li>
                <Link
                  to="/specialties/$slug"
                  params={{ slug: "cosmetic-gynaecology" }}
                  className="hover:text-accent"
                >
                  Cosmetic Gynecology
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "vaginoplasty" }}
                  className="hover:text-accent"
                >
                  Vaginoplasty
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "vaginal-tightening" }}
                  className="hover:text-accent"
                >
                  Vaginal Tightening
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "stress-urinary-incontinence" }}
                  className="hover:text-accent"
                >
                  Stress Urinary Incontinence
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "genital-warts" }}
                  className="hover:text-accent"
                >
                  Genital Warts
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "labiaplasty" }}
                  className="hover:text-accent"
                >
                  Labiaplasty
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "vaginal-prp" }}
                  className="hover:text-accent"
                >
                  Vaginal PRP
                </Link>
              </li>
              <li>
                <Link
                  to="/cosmetic-gynecology/$slug"
                  params={{ slug: "hymen-reconstruction" }}
                  className="hover:text-accent"
                >
                  Hymen Reconstruction
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Aesthetics */}
        <div className="mt-12">
          <h4 className="mb-4 font-display text-base font-semibold">Aesthetics &amp; Skin</h4>
          <ul className="grid gap-2 text-sm opacity-90 sm:grid-cols-2 lg:grid-cols-4">
            {aestheticTreatments
              .filter(
                (t) =>
                  ![
                    "chemical-peel",
                    "prp-hair-treatment",
                    "pigmentation-and-melasma-treatment",
                    "acne-and-acne-scar-treatment",
                    "tattoo-removal",
                    "anti-ageing-and-skin-rejuvenation",
                  ].includes(t.slug),
              )
              .map((t) => (
                <li key={t.slug}>
                  <Link
                    to="/aesthetics/$slug"
                    params={{ slug: t.slug }}
                    className="hover:text-accent"
                  >
                    {t.name}
                  </Link>
                </li>
              ))}
          </ul>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-sm sm:flex-row">
          <p className="opacity-90">
            © {new Date().getFullYear()} Healic Multispeciality Hospital. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="opacity-80">Follow Us</span>
            <a
              href="https://www.instagram.com/healichospital"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="opacity-80 hover:text-accent hover:opacity-100"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="opacity-80 hover:text-accent hover:opacity-100"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href="https://in.linkedin.com/company/healic-health"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="opacity-80 hover:text-accent hover:opacity-100"
            >
              <Linkedin className="h-5 w-5" />
            </a>
            <a
              href="https://youtube.com"
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="opacity-80 hover:text-accent hover:opacity-100"
            >
              <Youtube className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
